import { defineMessages, useIntl } from "react-intl";
import { Wrapper } from "../../components/layout/wrapper";
import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide";
import {IconStar, IconStarHalf } from "@tabler/icons-react";
import { AccommodationRelatedReviews, AccommodationReviewRates } from "shared/graphql/models/articles/rental copy";
import SpriteSvg from "../common/svg";

import "./reviews.scss";

const transDefs = defineMessages({
  title: { id: "article.review-title", defaultMessage: "Évaluations" },
  no_review: { id: "article.review.no-review", defaultMessage: "Ce bien n'a pas encore de commentaire." },
  opinion: { id: "artile.review-opinions", defaultMessage: "{count, plural, =1 {avis} other {avis}}" },
  welcome: {id: "article.review.welcome", defaultMessage: "Accueil"},
  location: {id: "article.review.location", defaultMessage: "Emplacement"},
  cleanliness: {id: "article.review.cleanliness", defaultMessage: "Propreté"},
  accuracy: {id: "article.review.accuracy", defaultMessage: "Exactitude"},
  reservation: {id: "article.review.reservation", defaultMessage: "Réservation"},
  overallComfort: {id: "article.review.overallComfort", defaultMessage: "Confort général"},
  bedsComfort: {id: "article.review.bedsComfort", defaultMessage: "Confort des lits"},
  value: {id: "article.review.value", defaultMessage: "Rapport qualité/prix"},
  satisfaction: {id: "article.review.satisfaction", defaultMessage: "Satisfaction"},
});


type Props = {
  loading: boolean;
  data?: {bookingAccommodationReviews: AccommodationRelatedReviews};
}

const carousel_option = {
  pagination: false,
  perPage:3,
  perMove: 1,
  gap:"4rem",
  padding:"0px",
  breakpoints: {
    1200:{
      perPage:2,
      gap:"3rem",
    },
    576:{
      perPage:1,
      gap:"1rem",
    },
  },
}

const formatRate = (rate: number): string => {
  return Number.isInteger(rate) ? String(rate) : rate.toFixed(2).replace(/0$/, "");
};

const Reviews = ({ loading, data }: Props) => {

  const intl = useIntl();

  const reviewRateLabels: Record<keyof Omit<AccommodationReviewRates, "__typename">, string> = {
    welcome: intl.formatMessage(transDefs.welcome),
    location: intl.formatMessage(transDefs.location),
    cleanliness: intl.formatMessage(transDefs.cleanliness),
    accuracy: intl.formatMessage(transDefs.accuracy),
    reservation: intl.formatMessage(transDefs.reservation),
    overallComfort: intl.formatMessage(transDefs.overallComfort),
    bedsComfort: intl.formatMessage(transDefs.bedsComfort),
    value: intl.formatMessage(transDefs.value),
    satisfaction: intl.formatMessage(transDefs.satisfaction),
  };

  const renderStars = (rate:number) => {
    const roundedRate = Math.floor(rate * 2) / 2;
    const fullStars = Math.floor(roundedRate);
    const hasHalfStar = roundedRate % 1 === 0.5;
    return(<>
      {Array.from({ length: fullStars }, (_, i) => (
        <IconStar key={`full-${i}`} />
      ))}

      {hasHalfStar && (
        <IconStarHalf />
      )}
    </>)
  };

  const renderReviewRates = (rates: AccommodationReviewRates) => {
    const entries = Object.entries(reviewRateLabels)
    .map(([key, label]) => {
      const value = rates[key as keyof AccommodationReviewRates];

      if (typeof value !== "number") return null;

      return {
        key,
        label,
        value,
        percent: Math.min(Math.max((value / 5) * 100, 0), 100),
      };
    })
    .filter(Boolean) as {
      key: string;
      label: string;
      value: number;
      percent: number;
    }[];

    return (
      <div className="review-rates">
        {entries.map((item) => (
          <div className="review-rates__row" key={item.key}>
            <span className="review-rates__label">{item.label}</span>

            <span className="review-rates__bar">
              <span
                className="review-rates__progress"
                style={{ width: `${item.percent}%` }}
              />
            </span>

            <span className="review-rates__value">
              {formatRate(item.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (!loading && data){
      return (<Wrapper.Centered>
        <h2>{intl.formatMessage(transDefs.title)}</h2>
        <div className="rate">
          <span >{data.bookingAccommodationReviews.averageRate}</span>
          <div className="stars">
            {renderStars(data.bookingAccommodationReviews.averageRate)}
          </div>
          <span className="count">({data.bookingAccommodationReviews.ratesCount} {intl.formatMessage(transDefs.opinion,{count: data.bookingAccommodationReviews.ratesCount})})</span>
        </div>
        {renderReviewRates(data.bookingAccommodationReviews.rates)}
        {data.bookingAccommodationReviews.comments.length < 1 && <p className="no-reviews">{intl.formatMessage(transDefs.no_review)}</p>}
        {data.bookingAccommodationReviews.comments.length > 0 && <Splide  hasTrack={ false }
                                                                          options={carousel_option}
                                                                          className="reviews-carousel">
          <div className="splide__arrows custom">
              <button className="splide__arrow splide__arrow--prev">
                <SpriteSvg id="chevron-right" />
              </button>
              <button className="splide__arrow splide__arrow--next">
                <SpriteSvg id="chevron-right" />
              </button>
          </div>
          <SplideTrack>
              {data.bookingAccommodationReviews.comments.map((item: any, index: number) => (
                <SplideSlide key={index} className="review">
                  <div className="head">
                    <div className="customer">
                      <h3>{item.customerName}</h3>
                      <span className="date">{intl.formatDate(new Date(item.reviewedAt), { year: "numeric", month: "long", day: "numeric" })}</span>
                    </div>
                    <div className="rate">
                      <span >{item.averageRate}</span>
                      <div className="stars">
                        {renderStars(item.averageRate)}
                      </div>
                    </div>
                  </div>
                  <div className="comment"><p>{item.comment}</p></div>
                </SplideSlide>
              ))}
          </SplideTrack>
          </Splide> }
      </Wrapper.Centered>)
  }

}

export default Reviews;
